import { computed, observable } from 'mobx';
import { Order, Patient, User } from '@/store/engine/openapi/generated';
import { Store } from '@/store/mainStore';
import UtilsStore from '@/store/utilsStore';

const sessionStatuses = [
	'SESSION_STATUS_SESSION_CREATED',
	'SESSION_STATUS_MEASUREMENT_DATA_CAPTURED',
	'SESSION_STATUS_PRESCRIPTION_CREATED',
	'SESSION_STATUS_PRESCRIPTION_COMPLETED',
	'SESSION_STATUS_PRESCRIPTION_UPDATED',
	'SESSION_STATUS_ORDER_DELIVERED',
	'SESSION_STATUS_ORDER_CANCELLED',
	'SESSION_STATUS_ORDER_CREATED',
	'SESSION_STATUS_REPAIR_ORDER_CREATED'
];

type SessionStatus = typeof sessionStatuses[number];

export type OrderWithPatient = { order: Order; patient: Patient };
export type OrderPagination = { count: number; limit: number; offset: number };
export type OrderFetch = { orders?: OrderWithPatient[]; pagination: OrderPagination };
export default class DoctorStore {
	@observable public doctorUser?: User;
	@observable public patients?: Patient[];
	@observable public orders?: Order[];
	@observable public ordersWithPatient?: OrderWithPatient[];
	@observable public ordersPagination: OrderPagination = { count: 0, limit: 0, offset: 0 };
	store: Store;

	private ordersFetchPromise: any = null;
	private patientsFetchPromise: any = null;

	constructor(store: Store) {
		this.store = store;
	}

	@computed get ordersPaginationPercentage() {
		// FIXME(HPCLINIC-382): Reenable once HPCLINIC-382 is fixed
		return 100; // this.ordersPagination.count ? ((this.ordersPagination.offset + this.ordersPagination.limit) / this.ordersPagination.count) * 100 : 0;
	}

	async fetchPatients() {
		if (this.patientsFetchPromise) {
			return await this.patientsFetchPromise;
		}
		this.patientsFetchPromise = this.store.engine.store.getPatients(0, 100000).then((p) => p && p.patients);
		const patients = await this.patientsFetchPromise;
		this.patientsFetchPromise = null;
		if (!patients) {
			console.error('getPatients returned null');
			return;
		}
		this.patients = patients.flatMap((f) => (f ? [f] : []));
		const p = (t) => Date.parse(t);
		if (this.patients) {
			this.patients.slice().sort((a: Patient, b: Patient) => {
				let timeA = p(a.medicalSolutionData?.lastUpdated);
				let timeB = p(b.medicalSolutionData?.lastUpdated);
				return timeB - timeA;
			});
		}

		return this.patients;
	}

	async fetchPatientOrders(): Promise<OrderFetch> {
		if (this.ordersFetchPromise) {
			return { orders: this.ordersWithPatient, pagination: this.ordersPagination };
		}

		if (!this.patients) {
			await this.fetchPatients();
		}

		let limit = 200;
		if (this.ordersPagination.count) {
			limit = UtilsStore.clamp(this.ordersPagination.count - (this.orders?.length || 0), 0, 200);
		}

		// nothing to fetch, we fetch orders once per session, if new order is added session is restarted
		if (!limit) {
			return { orders: this.ordersWithPatient, pagination: this.ordersPagination };
		}

		this.ordersFetchPromise = this.store.engine.store.getOrderList(limit, this.orders?.length || 0);
		const ordersData = await this.ordersFetchPromise;
		this.ordersFetchPromise = null;
		if (!ordersData || !ordersData.orders) {
			throw new Error('No orders fetched');
		}
		this.assignOrdersOwners(ordersData.orders.flatMap((f) => (f ? [f] : [])));
		this.ordersPagination = ordersData.pagination;
		return { orders: this.ordersWithPatient, pagination: this.ordersPagination };
	}

	private assignOrdersOwners(orders: Order[]) {
		if (!this.orders) {
			this.orders = [];
		}

		const removeDuplicatedOrders = new Set([...orders]);
		this.orders = this.orders.concat(orders);
		orders.forEach((o) => {
			if (!this.ordersWithPatient) {
				this.ordersWithPatient = [];
			}
			const findOrderPatient = this.patients?.find((p) => (p.user ? p.user.userID === o.userID : undefined));
			if (!findOrderPatient) {
				// console.error(`Cannot find order ${o.orderID} patient: order user id: ${o.userID}`);
				return;
			}
			this.ordersWithPatient.push({ order: o, patient: findOrderPatient });
		});

		if (this.ordersWithPatient) {
			const p = (t) => Date.parse(t);
			this.ordersWithPatient.slice().sort((a: OrderWithPatient, b: OrderWithPatient) => {
				let timeA = p(a.order.createdAt);
				let timeB = p(b.order.createdAt);
				return timeB - timeA;
			});
		}
	}

	public async getPatientFromID(id: string) {
		return this.store.engine.store
			.getPatient(id)
			.then((p) => p && p)
			.catch((e) => console.error(`Cannot get patient of id ${id}`));
	}

	public async getPatients() {
		return this.fetchPatients();
	}

	public deletePatient(patient: Patient) {
		this.patients = this.patients?.filter((i) => i.user?.userID !== patient.user?.userID);
	}

	public async getPatientsOrders() {
		return this.ordersWithPatient;
	}

	public async findOrder(orderID: string) {
		if (!this.ordersWithPatient) {
			await this.fetchPatientOrders();
			await UtilsStore.wait(100);
			return this.findOrder(orderID);
		}
		const order = this.ordersWithPatient.find((i) => i.order.orderID === orderID);

		if (order) {
			return order;
		}

		await UtilsStore.wait(100);
		return this.ordersPaginationPercentage < 100 ? this.findOrder(orderID) : undefined;
	}
}
